import { useState, useEffect } from 'react';
import { NAV_ITEMS } from '@/lib/index';
import { Menu, X } from 'lucide-react';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-background/90 backdrop-blur-md border-b border-border/40 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); scrollTo('#hero'); }}
          className="flex flex-col leading-none group"
        >
          <span
            style={{ fontFamily: "'Special Elite', serif" }}
            className="text-2xl font-medium tracking-widest text-foreground uppercase"
          >
            Gaia Rise
          </span>
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground uppercase mt-0.5">
            Conscious Living Protocol
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className="text-sm tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-300 uppercase"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground p-1"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-md border-t border-border/40 px-6 py-6 flex flex-col gap-5">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className="text-sm tracking-widest text-left text-muted-foreground hover:text-foreground transition-colors uppercase"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-card border-t border-border/30 py-12 px-6">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start gap-8">
          {/* Brand */}
          <div className="flex flex-col gap-2">
            <span
              style={{ fontFamily: "'Special Elite', serif" }}
              className="text-xl font-medium tracking-widest text-foreground uppercase"
            >
              Gaia Rise
            </span>
            <p className="text-xs tracking-widest text-muted-foreground uppercase">
              Conscious Living Protocol
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Caribbean Islands · Based in Saint-Martin
            </p>
            <div className="mt-4 pt-4 border-t border-border/20 space-y-1">
              <p className="text-xs text-muted-foreground">Silvia Dalu</p>
              <p className="text-xs text-muted-foreground/70 italic">Founder — GAIA RISE</p>
              <p className="text-xs text-muted-foreground/60">Sustainable Solutions for Conscious Living</p>
              <p className="text-sm tracking-widest text-primary font-semibold mt-2">Water · Earth · Human Consciousness</p>
            </div>
          </div>

          {/* Contact */}
          <div className="flex flex-col gap-2">
            <span className="text-xs tracking-widest text-muted-foreground uppercase mb-1">
              Contact
            </span>
            <a
              href="mailto:gaiarise.collective@proton.me"
              className="text-sm text-foreground hover:text-primary transition-colors"
            >
              gaiarise.collective@proton.me
            </a>
            <a
              href="https://gaiarise.eco"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              gaiarise.eco
            </a>
          </div>

          {/* SDGs note */}
          <div className="flex flex-col gap-2 max-w-xs">
            <span className="text-xs tracking-widest text-muted-foreground uppercase mb-1">
              Our Commitment
            </span>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Contributing to UN Sustainable Development Goals: Zero Hunger (2), 
              Good Health & Well-being (3), Clean Water & Sanitation (6).
            </p>
          </div>
        </div>

        <div className="border-t border-border/30 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground">
            © 2026 Gaia Rise. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground italic">
            Born in the Caribbean. Built with intention.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
